
import { useNavigate } from 'react-router-dom';
import React from 'react';

interface OrderConfirmationProps {
  /** Unique order reference string */
  orderRef: string;
  /** Order total amount (in USD) */
  total: number;
}

/**
 * OrderConfirmation component displays a confirmation screen after a successful checkout.
 * It includes navigation back to the shop or home page and robust handling for unexpected props.
 */
export default function OrderConfirmation({
  orderRef,
  total,
}: OrderConfirmationProps): JSX.Element {
  const navigate = useNavigate();

  /**
   * Safely format the total amount.
   * If `total` is not a valid number, fall back to "0.00".
   */
  const formattedTotal = React.useMemo(() => {
    const amount = Number(total);
    return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
  }, [total]);

  /**
   * Guard against missing order reference.
   * In production we would log this to an error monitoring service.
   */
  if (!orderRef) {
    console.error('OrderConfirmation: Missing orderRef prop');
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Header */}
      <header className="bg-[#111] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/3b1e60a7c97fafd8931cbea82c7dfd99.png"
              alt="Nightmare Logo"
              className="w-16 h-16 object-contain"
            />
          </button>

          <div className="hidden md:flex items-center gap-3 text-sm font-semibold uppercase tracking-widest">
            <span className="text-gray-600">Cart</span>
            <i className="ri-arrow-right-s-line text-gray-700" />
            <span className="text-gray-600">Checkout</span>
            <i className="ri-arrow-right-s-line text-gray-700" />
            <span className="text-[#8B0000]">Confirmation</span>
          </div>

          <div className="w-32" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full text-center">
          {/* Success Icon */}
          <div className="relative inline-flex items-center justify-center mb-8">
            <div className="w-28 h-28 rounded-full bg-[#8B0000]/10 border-2 border-[#8B0000]/30 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[#8B0000]/20 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-5xl text-[#8B0000]" />
              </div>
            </div>
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full border-2 border-[#8B0000]/20 animate-ping" />
          </div>

          <h1 className="text-white text-4xl font-bold mb-3 uppercase tracking-wider">
            Order Placed!
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Thanks for your order. We&apos;ll reach out shortly with payment and shipping details.
          </p>

          {/* Order Card */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-6 mb-8 text-left space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm uppercase tracking-wider">
                Order Reference
              </span>
              <span className="text-[#8B0000] font-bold text-lg tracking-widest">{orderRef || '—'}</span>
            </div>

            <div className="border-t border-gray-800 pt-4 flex items-center justify-between">
              <span className="text-gray-500 text-sm uppercase tracking-wider">
                Order Total
              </span>
              <span className="text-white font-bold text-lg">${formattedTotal}</span>
            </div>

            <div className="border-t border-gray-800 pt-4 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-mail-send-line text-[#8B0000] text-xl" />
              </div>
              <p className="text-gray-400 text-sm">
                A confirmation email will be sent to you with next steps.
              </p>
            </div>
          </div>

          {/* What's Next */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-6 mb-8 text-left">
            <h3 className="text-white font-bold uppercase tracking-wider mb-4 text-sm">
              What Happens Next
            </h3>
            <div className="space-y-4">
              {[
                {
                  icon: 'ri-mail-line',
                  title: 'Check Your Email',
                  desc: "We'll send payment instructions within 24 hours.",
                },
                {
                  icon: 'ri-bank-card-line',
                  title: 'Complete Payment',
                  desc: 'Pay via Venmo, Cash App, PayPal, or credit card.',
                },
                {
                  icon: 'ri-truck-line',
                  title: 'We Ship Your Order',
                  desc: 'Your Nightmare gear ships within 3–5 business days.',
                },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#8B0000]/10 border border-[#8B0000]/30 rounded-lg flex-shrink-0">
                    <i className={`${step.icon} text-[#8B0000] text-lg`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{step.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex-1 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold py-4 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wider"
            >
              <i className="ri-store-2-line mr-2" />
              Back to Shop
            </button>

            <button
              onClick={() => navigate('/')}
              className="flex-1 border border-gray-700 hover:border-[#8B0000] text-gray-400 hover:text-[#8B0000] font-semibold py-4 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wider text-sm"
            >
              <i className="ri-home-line mr-2" />
              Go Home
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
